import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Eraser, ImageUp, Undo2 } from 'lucide-react';
import { lift, paint, penDown, penMove, takeBack, type PadPoint, type Stroke } from '../lib/padStrokes.js';
import { Button, IconButton } from './ui.js';

/**
 * The pad.
 *
 * Pointer events rather than mouse plus touch: one code path covers a finger on
 * a phone, a stylus on a tablet and a mouse on a laptop, and `setPointerCapture`
 * is what keeps a stroke attached to the pad when the finger slides past its
 * edge mid-signature. `touch-action: none` is not decoration — without it the
 * browser treats the first drag as a scroll and the customer's first attempt to
 * sign moves the page instead.
 *
 * It lives here rather than on the signing page because a document can now ask
 * to be signed in its own body as well as at its end, and a second pad written
 * separately would be a second set of these details to get right.
 *
 * 1.6 — it keeps its strokes as well as drawing them, so "חזור" beside clear
 * takes back the last one: a signature that went wrong on its last letter is
 * mended there, not signed again from the first. The canvas only ever shows
 * what the kept strokes draw, and the page is told after an undo exactly as it
 * is after a stroke or a clear.
 *
 * 1.28 — the sender's own pad is laid out another way, and asks for it with
 * `signHere` and `iconButtons`. Both are off for the signer's page and the
 * digital document, which look as they always have.
 *
 * 1.50 — the signer's page asks for one thing of its own, `upload`: an icon in
 * the frame's bottom end corner to pick a picture of a stamp or a signature
 * with. The pad hands the file over and no more.
 */
export function SignaturePad({
  onChange,
  disabled = false,
  clearLabel,
  className = 'signature-pad',
  canvas,
  signHere,
  iconButtons = false,
  upload,
}: {
  onChange: (dataUrl: string | null) => void;
  disabled?: boolean;
  clearLabel: string;
  className?: string;
  /**
   * Identity and description for the element itself — label, error wiring, and
   * the marker the signing page uses to put focus on the first field it is
   * still waiting for.
   */
  canvas?: React.CanvasHTMLAttributes<HTMLCanvasElement> & { 'data-document-field'?: string };
  /** Faded words in the middle of the pad, there until the first stroke. */
  signHere?: string;
  /**
   * Undo and clear as icons in the pad's bottom start corner, inside its
   * frame, instead of the buttons with words in a row under it.
   */
  iconButtons?: boolean;
  /**
   * An icon in the frame's bottom end corner that opens the picker for a PNG or
   * JPEG, from the files or the camera. Reading it, and what it is for, are
   * the page's; with this absent the pad is as it was.
   */
  upload?: { label: string; onPick: (file: File) => void };
}) {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);
  /* The finished strokes, oldest first. */
  const strokes = useRef<Stroke[]>([]);
  /* The stroke being drawn, from the pen coming down until it lifts. */
  const drawing = useRef<PadPoint[] | null>(null);
  /* How many strokes there are to take back — state, for the button to follow. */
  const [color, setColor] = useState('#1d1d1f');
  const [width, setWidth] = useState(2.2);
  const [kept, setKept] = useState(0);
  /* The pen is down — state, for the faded words to go as it lands. */
  const [inking, setInking] = useState(false);

  /*
   * The canvas is sized from its own box in device pixels once it is laid out.
   * A canvas left at its default 300x150 and stretched by CSS records the
   * strokes at that resolution, and the signature that comes back out is the
   * blurred, stretched version of what the customer drew.
   */
  const resize = useCallback(() => {
    const element = canvasRef.current;
    if (!element) return;

    const ratio = window.devicePixelRatio || 1;
    const rect = element.getBoundingClientRect();
    if (rect.width === 0) return;

    element.width = Math.round(rect.width * ratio);
    element.height = Math.round(rect.height * ratio);

    const context = element.getContext('2d');
    if (!context) return;
    context.scale(ratio, ratio);
    context.lineWidth = 2.2;
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.strokeStyle = '#1d1d1f';
    // Sizing the canvas wiped it. The strokes are kept in the pad's own CSS
    // pixels, so they go back on at the new density, and the one being drawn
    // goes last for the pen to carry on from where it is.
    paint(context, drawing.current ? [...strokes.current, drawing.current] : strokes.current);
  }, []);

  useEffect(() => {
    resize();
    // A phone turned sideways mid-signature re-lays the pad out, and the new
    // buffer starts empty; resize draws the strokes back onto it, so the pad
    // still shows what it last handed the page and an undo after it still
    // takes back only the last stroke.
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [resize]);

  const point = (event: React.PointerEvent<HTMLCanvasElement>): PadPoint => {
    const rect = event.currentTarget.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  /* What the pad holds, handed to the page: the drawing, or nothing once no stroke is left. */
  const report = () =>
    onChange(strokes.current.length > 0 ? (canvasRef.current?.toDataURL('image/png') ?? null) : null);

  const start = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (disabled) return;
    const context = canvasRef.current?.getContext('2d');
    if (!context) return;

    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      // Some pointers cannot be captured — a synthetic event, a pointer the
      // browser has already released. The stroke still draws; it just stops at
      // the edge of the pad instead of following the finger past it.
    }
    const at = point(event);
    drawing.current = Object.assign([at], { color, width });
    context.strokeStyle = color;
    context.lineWidth = width;
    penDown(context, at);
    setInking(true);
  };

  const move = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const stroke = drawing.current;
    if (!stroke) return;
    const context = canvasRef.current?.getContext('2d');
    if (!context) return;

    const at = point(event);
    stroke.push(at);
    penMove(context, at);
  };

  const end = () => {
    const stroke = drawing.current;
    if (!stroke) return;
    drawing.current = null;
    strokes.current = lift(strokes.current, stroke);
    setKept(strokes.current.length);
    setInking(false);
    report();
  };

  const clear = () => {
    const element = canvasRef.current;
    const context = element?.getContext('2d');
    if (!element || !context) return;

    context.clearRect(0, 0, element.width, element.height);
    strokes.current = [];
    setKept(0);
    report();
  };

  /* 1.6 — the last stroke taken back, and the rest drawn again as they were drawn. */
  const undo = () => {
    const element = canvasRef.current;
    const context = element?.getContext('2d');
    if (!element || !context || strokes.current.length === 0) return;

    strokes.current = takeBack(strokes.current);
    context.clearRect(0, 0, element.width, element.height);
    paint(context, drawing.current ? [...strokes.current, drawing.current] : strokes.current);
    setKept(strokes.current.length);
    report();
  };

  const surface = (
    <canvas
      {...canvas}
      ref={canvasRef}
      className={className}
      style={{ touchAction: 'none' }}
      onPointerDown={start}
      onPointerMove={move}
      onPointerUp={end}
      onPointerCancel={end}
    />
  );

  /* The controls sit on a pad that is white in every theme, so each has a ground of its own. */
  const iconClass = 'bg-white/90 text-neutral-800 disabled:opacity-40 disabled:pointer-events-none';

  return (
    <div className="stack">
      {signHere !== undefined || iconButtons || upload ? (
        // A flex box, so the canvas is a block and not a line of text with a gap under it.
        <div className="relative flex">
          {surface}
          {/* Over the canvas and out of the finger's way: the press goes through to the pad. */}
          {signHere !== undefined && kept === 0 && !inking && (
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 flex select-none items-center justify-center text-2xl font-medium text-neutral-400"
            >
              {signHere}
            </span>
          )}
          {iconButtons && (
            <div className="absolute bottom-1.5 start-1.5 flex gap-1.5">
              <IconButton
                type="button"
                label={t('publicSign.undo')}
                className={iconClass}
                onClick={undo}
                disabled={disabled || kept === 0}
              >
                <Undo2 className="h-[18px] w-[18px]" aria-hidden />
              </IconButton>
              <IconButton type="button" label={clearLabel} className={iconClass} onClick={clear} disabled={disabled}>
                <Eraser className="h-[18px] w-[18px]" aria-hidden />
              </IconButton>
            </div>
          )}
          {upload && (
            <div className="absolute bottom-1.5 end-1.5">
              <IconButton
                type="button"
                label={upload.label}
                className={iconClass}
                onClick={() => fileInput.current?.click()}
                disabled={disabled}
              >
                <ImageUp className="h-[18px] w-[18px]" aria-hidden />
              </IconButton>
              <input
                ref={fileInput}
                hidden
                type="file"
                accept="image/png,image/jpeg"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  // Picking the same file again is still a change.
                  event.target.value = '';
                  if (file) upload.onPick(file);
                }}
              />
            </div>
          )}
        </div>
      ) : (
        surface
      )}
      {!iconButtons && (
        <div className="row row--end">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            icon={<Undo2 className="h-4 w-4" aria-hidden />}
            onClick={undo}
            disabled={disabled || kept === 0}
          >
            {t('publicSign.undo')}
          </Button>
          <Button size="sm" variant="ghost" onClick={clear} disabled={disabled}>
            {clearLabel}
          </Button>
        </div>
      )}
      <div className="flex items-center gap-2" role="group" aria-label={t('signing.inkColor')}>
        {['#1d1d1f','#1d4ed8'].map(ink=><button key={ink} type="button" disabled={disabled} aria-label={t(ink==='#1d1d1f'?'signing.inkBlack':'signing.inkBlue')} aria-pressed={color===ink} onClick={()=>setColor(ink)} className="h-7 w-7 rounded-full border-2 border-white ring-1 ring-border" style={{background:ink}}/>)}
        <input type="range" min="1" max="6" step="0.2" aria-label={t('signing.inkWidth')} value={width} disabled={disabled} onChange={event=>setWidth(Number(event.target.value))} className="w-24 accent-primary"/>
      </div>
    </div>
  );
}
